/**
 * Indicatifs téléphoniques et règles de saisie des numéros patients.
 *
 * Le numéro est stocké en E.164 (`+224620000099`) : c'est sous cette forme
 * que WhatsApp identifie le patient. Le nom des pays vient du navigateur
 * (Intl.DisplayNames), la liste ne porte que le code ISO et l'indicatif.
 */

export const INDICATIF_DEFAUT = '224';

// [code ISO 3166, indicatif]
const INDICATIFS: [string, string][] = [
  ['AF', '93'], ['ZA', '27'], ['AL', '355'], ['DZ', '213'], ['DE', '49'], ['AD', '376'], ['AO', '244'],
  ['AG', '1268'], ['SA', '966'], ['AR', '54'], ['AM', '374'], ['AU', '61'], ['AT', '43'], ['AZ', '994'],
  ['BS', '1242'], ['BH', '973'], ['BD', '880'], ['BB', '1246'], ['BE', '32'], ['BZ', '501'], ['BJ', '229'],
  ['BT', '975'], ['BY', '375'], ['BO', '591'], ['BA', '387'], ['BW', '267'], ['BR', '55'], ['BN', '673'],
  ['BG', '359'], ['BF', '226'], ['BI', '257'], ['KH', '855'], ['CM', '237'], ['CA', '1'], ['CV', '238'],
  ['CF', '236'], ['CL', '56'], ['CN', '86'], ['CY', '357'], ['CO', '57'], ['KM', '269'], ['CG', '242'],
  ['CD', '243'], ['KR', '82'], ['KP', '850'], ['CR', '506'], ['CI', '225'], ['HR', '385'], ['CU', '53'],
  ['DK', '45'], ['DJ', '253'], ['DM', '1767'], ['EG', '20'], ['AE', '971'], ['EC', '593'], ['ER', '291'],
  ['ES', '34'], ['EE', '372'], ['SZ', '268'], ['US', '1'], ['ET', '251'], ['FJ', '679'], ['FI', '358'],
  ['FR', '33'], ['GA', '241'], ['GM', '220'], ['GE', '995'], ['GH', '233'], ['GR', '30'], ['GD', '1473'],
  ['GT', '502'], ['GN', '224'], ['GW', '245'], ['GQ', '240'], ['GY', '592'], ['HT', '509'], ['HN', '504'],
  ['HK', '852'], ['HU', '36'], ['IN', '91'], ['ID', '62'], ['IQ', '964'], ['IR', '98'], ['IE', '353'],
  ['IS', '354'], ['IL', '972'], ['IT', '39'], ['JM', '1876'], ['JP', '81'], ['JO', '962'], ['KZ', '7'],
  ['KE', '254'], ['KG', '996'], ['KI', '686'], ['KW', '965'], ['LA', '856'], ['LS', '266'], ['LV', '371'],
  ['LB', '961'], ['LR', '231'], ['LY', '218'], ['LI', '423'], ['LT', '370'], ['LU', '352'], ['MO', '853'],
  ['MK', '389'], ['MG', '261'], ['MY', '60'], ['MW', '265'], ['MV', '960'], ['ML', '223'], ['MT', '356'],
  ['MA', '212'], ['MH', '692'], ['MU', '230'], ['MR', '222'], ['MX', '52'], ['FM', '691'], ['MD', '373'],
  ['MC', '377'], ['MN', '976'], ['ME', '382'], ['MZ', '258'], ['MM', '95'], ['NA', '264'], ['NR', '674'],
  ['NP', '977'], ['NI', '505'], ['NE', '227'], ['NG', '234'], ['NO', '47'], ['NZ', '64'], ['OM', '968'],
  ['UG', '256'], ['UZ', '998'], ['PK', '92'], ['PW', '680'], ['PS', '970'], ['PA', '507'], ['PG', '675'],
  ['PY', '595'], ['NL', '31'], ['PE', '51'], ['PH', '63'], ['PL', '48'], ['PT', '351'], ['QA', '974'],
  ['DO', '1809'], ['CZ', '420'], ['RO', '40'], ['GB', '44'], ['RU', '7'], ['RW', '250'], ['KN', '1869'],
  ['SM', '378'], ['VC', '1784'], ['LC', '1758'], ['SB', '677'], ['SV', '503'], ['WS', '685'], ['ST', '239'],
  ['SN', '221'], ['RS', '381'], ['SC', '248'], ['SL', '232'], ['SG', '65'], ['SK', '421'], ['SI', '386'],
  ['SO', '252'], ['SD', '249'], ['SS', '211'], ['LK', '94'], ['SE', '46'], ['CH', '41'], ['SR', '597'],
  ['SY', '963'], ['TJ', '992'], ['TW', '886'], ['TZ', '255'], ['TD', '235'], ['TH', '66'], ['TL', '670'],
  ['TG', '228'], ['TO', '676'], ['TT', '1868'], ['TN', '216'], ['TM', '993'], ['TR', '90'], ['TV', '688'],
  ['UA', '380'], ['UY', '598'], ['VU', '678'], ['VA', '379'], ['VE', '58'], ['VN', '84'], ['YE', '967'],
  ['ZM', '260'], ['ZW', '263'],
];

export interface Pays {
  iso: string;
  indicatif: string;
  nom: string;
}

const nomsPays = (() => {
  try {
    // Intl.DisplayNames n'est pas dans la lib TS « es6 » du projet
    return new (Intl as any).DisplayNames(['fr'], { type: 'region' });
  } catch {
    return null;
  }
})();

/** Guinée en tête, puis ordre alphabétique français. */
export const PAYS: Pays[] = INDICATIFS
  .map(([iso, indicatif]) => ({ iso, indicatif, nom: nomsPays?.of(iso) ?? iso }))
  .sort((a, b) =>
    a.indicatif === INDICATIF_DEFAUT ? -1 : b.indicatif === INDICATIF_DEFAUT ? 1 : a.nom.localeCompare(b.nom, 'fr'));

/**
 * Découpe un numéro enregistré en indicatif + numéro national.
 * Indicatifs sans préfixe commun (E.164) : le plus long qui correspond l'emporte.
 * Un numéro sans « + » (ancienne saisie) est considéré comme guinéen.
 */
export const decouperNumero = (valeur: string | undefined): { indicatif: string; national: string } => {
  const brut = (valeur ?? '').trim();
  const chiffres = brut.replace(/\D/g, '');
  if (!brut.startsWith('+')) return { indicatif: INDICATIF_DEFAUT, national: chiffres };
  const indicatif = INDICATIFS
    .map(([, code]) => code)
    .filter(code => chiffres.startsWith(code))
    .sort((a, b) => b.length - a.length)[0];
  return indicatif
    ? { indicatif, national: chiffres.slice(indicatif.length) }
    : { indicatif: INDICATIF_DEFAUT, national: chiffres };
};

export const composerNumero = (indicatif: string, national: string): string => {
  const chiffres = national.replace(/\D/g, '').replace(/^0+/, '');
  return chiffres ? `+${indicatif}${chiffres}` : '';
};

/** Message d'erreur, ou `true` si le numéro est valide (format `validate` de react-hook-form). */
export const validerNumero = (valeur: string | undefined): true | string => {
  const { indicatif, national } = decouperNumero(valeur);
  if (!national) return 'Le téléphone est requis';
  if (indicatif === INDICATIF_DEFAUT) {
    return national.length === 9 || 'Un numéro guinéen compte 9 chiffres';
  }
  return (national.length >= 6 && indicatif.length + national.length <= 15)
    || 'Le numéro doit compter au moins 6 chiffres';
};
